import { ApolloClient } from '@apollo/client'

import { CommunityShowView_CommunityDocument } from '@/graphql/api/generated'

/**
 * Extracts the circle IRI from a string or context object.
 * Returns null if the value is not a circle IRI (e.g. "/circles/my-slug").
 */
function extractCircleIri(iri: string | undefined | null): string | null {
  if (!iri || typeof iri !== 'string') return null
  const trimmed = iri.trim()
  if (!trimmed.includes('/circles/')) return null
  return trimmed
}

/**
 * Refetches CommunityShowView_Community when the user receives a join
 * acceptance/rejection notification while on the community page.
 * Ensures membership status (e.g. Join button vs member actions) stays in sync.
 *
 * @param client - Apollo Client instance
 * @param circleId - Circle IRI (e.g. "/circles/my-slug") or context object with circleId
 */
export async function refetchCommunityShowViewQuery(
  client: ApolloClient,
  circleId: string | { circleId?: string } | undefined | null
): Promise<void> {
  let circleIri: string | null = null

  if (typeof circleId === 'string') {
    circleIri = extractCircleIri(circleId)
  } else if (
    circleId &&
    typeof circleId === 'object' &&
    'circleId' in circleId
  ) {
    circleIri = extractCircleIri(circleId.circleId)
  }

  if (!circleIri) return

  try {
    await client.query({
      query: CommunityShowView_CommunityDocument,
      variables: { id: circleIri, withCurrentUserMemberships: true },
      fetchPolicy: 'network-only',
    })
  } catch (error) {
    console.warn('[refetchCommunityShowViewQuery] Failed to refetch:', error)
  }
}
